/**
 * Servizio API centralizzato per comunicare con il plugin WordPress custom
 */

import { WOOCOMMERCE_CONFIG } from './woocommerce';

const API_BASE_URL = WOOCOMMERCE_CONFIG.baseUrl;
const API_PATH = '/wp-json/braceesapori/v1';

/**
 * Ottiene il token di autenticazione dal localStorage
 */
const getAuthToken = (): string | null => {
  const authData = localStorage.getItem('braceesapori_auth');
  if (authData) {
    try {
      const { token } = JSON.parse(authData);
      return token;
    } catch {
      return null;
    }
  }
  return null;
};

/**
 * Esegue una richiesta API con gestione errori
 */
const apiRequest = async (
  endpoint: string,
  options: RequestInit = {}
): Promise<Response> => {
  const token = getAuthToken();
  
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...options.headers,
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  const response = await fetch(`${API_BASE_URL}${API_PATH}${endpoint}`, {
    ...options,
    headers,
  });
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Errore sconosciuto' }));
    throw new Error(error.message || `Errore ${response.status}`);
  }
  
  return response;
};

// ========== AUTENTICAZIONE ==========

export interface LoginResponse {
  success: boolean;
  token: string;
  user: {
    id: number;
    email: string;
    display_name: string;
    first_name: string;
    last_name: string;
  };
}

export interface RegisterData {
  email: string;
  password: string;
  first_name?: string;
  last_name?: string;
}

export const authApi = {
  /**
   * Login utente
   */
  login: async (username: string, password: string): Promise<LoginResponse> => {
    const response = await apiRequest('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    });
    return response.json();
  },

  /**
   * Registrazione nuovo utente
   */
  register: async (data: RegisterData): Promise<LoginResponse> => {
    const response = await apiRequest('/auth/register', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response.json();
  },

  /**
   * Richiedi reset password
   */
  lostPassword: async (email: string): Promise<{ success: boolean; message: string }> => {
    const response = await apiRequest('/auth/lost-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
    return response.json();
  },

  /**
   * Reset password con chiave
   */
  resetPassword: async (
    key: string,
    login: string,
    password: string
  ): Promise<{ success: boolean; message: string }> => {
    const response = await apiRequest('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ key, login, password }),
    });
    return response.json();
  },

  /**
   * Valida token JWT
   */
  validateToken: async (): Promise<{ valid: boolean; user?: any }> => {
    const response = await apiRequest('/auth/validate');
    return response.json();
  },
};

// ========== PROFILO UTENTE ==========

export interface UserProfile {
  id: number;
  email: string;
  display_name: string;
  first_name: string;
  last_name: string;
  billing: {
    first_name: string;
    last_name: string;
    company: string;
    address_1: string;
    address_2: string;
    city: string;
    postcode: string;
    country: string;
    state: string;
    email: string;
    phone: string;
  };
  shipping: {
    first_name: string;
    last_name: string;
    company: string;
    address_1: string;
    address_2: string;
    city: string;
    postcode: string;
    country: string;
    state: string;
  };
}

export interface UpdateProfileData {
  first_name?: string;
  last_name?: string;
  display_name?: string;
  billing?: Partial<UserProfile['billing']>;
  shipping?: Partial<UserProfile['shipping']>;
}

export const userApi = {
  /**
   * Ottieni profilo utente corrente
   */
  getProfile: async (): Promise<UserProfile> => {
    const response = await apiRequest('/user/profile');
    return response.json();
  },

  /**
   * Aggiorna profilo utente
   */
  updateProfile: async (data: UpdateProfileData): Promise<UserProfile> => {
    const response = await apiRequest('/user/profile', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return response.json();
  },

  /**
   * Cambia password
   */
  changePassword: async (
    currentPassword: string,
    newPassword: string
  ): Promise<{ success: boolean; message: string }> => {
    const response = await apiRequest('/user/change-password', {
      method: 'POST',
      body: JSON.stringify({
        current_password: currentPassword,
        new_password: newPassword,
      }),
    });
    return response.json();
  },
};

// ========== INDIRIZZI ==========

export interface Address {
  first_name: string;
  last_name: string;
  company: string;
  address_1: string;
  address_2: string;
  city: string;
  postcode: string;
  country: string;
  state: string;
  phone?: string;
  email?: string;
}

export interface AddressesResponse {
  billing: Address;
  shipping: Address;
}

export const addressesApi = {
  /**
   * Ottieni indirizzi utente
   */
  getAddresses: async (): Promise<AddressesResponse> => {
    const response = await apiRequest('/user/addresses');
    return response.json();
  },

  /**
   * Aggiungi/Aggiorna indirizzo
   */
  saveAddress: async (
    type: 'billing' | 'shipping',
    address: Partial<Address>
  ): Promise<AddressesResponse> => {
    const response = await apiRequest('/user/addresses', {
      method: 'POST',
      body: JSON.stringify({ type, ...address }),
    });
    return response.json();
  },

  /**
   * Aggiorna indirizzo esistente
   */
  updateAddress: async (
    id: number,
    type: 'billing' | 'shipping',
    address: Partial<Address>
  ): Promise<AddressesResponse> => {
    const response = await apiRequest(`/user/addresses/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ type, ...address }),
    });
    return response.json();
  },

  /**
   * Elimina indirizzo
   */
  deleteAddress: async (
    id: number,
    type: 'billing' | 'shipping'
  ): Promise<{ success: boolean; message: string }> => {
    const response = await apiRequest(`/user/addresses/${id}?type=${type}`, {
      method: 'DELETE',
    });
    return response.json();
  },
};

// ========== ORDINI ==========

export interface Order {
  id: number;
  status: string;
  currency: string;
  date_created: string;
  total: string;
  line_items: Array<{
    id: number;
    name: string;
    quantity: number;
    price: string;
    total: string;
  }>;
  billing: Address;
  shipping: Address;
  payment_method_title: string;
}

export interface OrdersResponse {
  orders: Order[];
  total: number;
  totalPages: number;
}

export const ordersApi = {
  /**
   * Ottieni ordini utente corrente
   */
  getUserOrders: async (page: number = 1, perPage: number = 10): Promise<OrdersResponse> => {
    const response = await apiRequest(`/user/orders?page=${page}&per_page=${perPage}`);
    const orders = await response.json();
    const total = parseInt(response.headers.get('X-WP-Total') || '0');
    const totalPages = parseInt(response.headers.get('X-WP-TotalPages') || '1');
    
    return {
      orders: Array.isArray(orders) ? orders : [],
      total,
      totalPages,
    };
  },

  /**
   * Ottieni singolo ordine
   */
  getOrder: async (orderId: number): Promise<Order> => {
    const response = await apiRequest(`/user/orders/${orderId}`);
    return response.json();
  },

  /**
   * Crea nuovo ordine
   */
  createOrder: async (orderData: any): Promise<Order> => {
    const response = await apiRequest('/orders', {
      method: 'POST',
      body: JSON.stringify(orderData),
    });
    return response.json();
  },

  /**
   * Ottieni URL pagamento ordine
   */
  getOrderPaymentUrl: async (orderId: number): Promise<{ payment_url: string; order_id: number; status: string }> => {
    const response = await apiRequest(`/orders/${orderId}/payment`);
    return response.json();
  },

  /**
   * Ottieni stato ordine
   */
  getOrderStatus: async (orderId: number): Promise<{ order_id: number; status: string; total: string; currency: string }> => {
    const response = await apiRequest(`/orders/${orderId}/status`);
    return response.json();
  },

  /**
   * Ottieni rimborsi di un ordine
   */
  getOrderRefunds: async (orderId: number): Promise<{
    order_id: number;
    refunds: Array<{
      id: number;
      amount: string;
      reason: string;
      date_created: string;
      status: string;
    }>;
    total_refunded: string;
    refundable_amount: number;
  }> => {
    const response = await apiRequest(`/orders/${orderId}/refunds`);
    return response.json();
  },

  /**
   * Richiedi rimborso per un ordine
   */
  requestRefund: async (
    orderId: number,
    amount?: number,
    reason?: string
  ): Promise<{
    success: boolean;
    message: string;
    order_id: number;
    requested_amount: number;
    refundable_amount: number;
  }> => {
    const response = await apiRequest(`/orders/${orderId}/refund-request`, {
      method: 'POST',
      body: JSON.stringify({ amount, reason }),
    });
    return response.json();
  },
};

// ========== PRODOTTI (Proxy WooCommerce) ==========

export const productsApi = {
  /**
   * Ottieni lista prodotti (usa proxy)
   */
  getProducts: async (params?: {
    page?: number;
    per_page?: number;
    category?: number;
    search?: string;
    orderby?: string;
    order?: 'asc' | 'desc';
  }): Promise<any> => {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          queryParams.append(key, value.toString());
        }
      });
    }
    
    const response = await apiRequest(`/products?${queryParams.toString()}`);
    return response.json();
  },

  /**
   * Ottieni singolo prodotto (usa proxy)
   */
  getProduct: async (productId: number): Promise<any> => {
    const response = await apiRequest(`/products/${productId}`);
    return response.json();
  },

  /**
   * Ottieni categorie (usa proxy)
   */
  getCategories: async (params?: { per_page?: number; orderby?: string; order?: 'asc' | 'desc' }): Promise<any> => {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          queryParams.append(key, value.toString());
        }
      });
    }
    
    const response = await apiRequest(`/products/categories?${queryParams.toString()}`);
    return response.json();
  },
};

